// Main JavaScript file

import Tracking from './tracking.js';
import TrackingCore from './tracking-core.js';
import { TRACKING_EVENTS } from './tracking-constants.js';
import PopupManager from './popup-manager.js';
import SubscribePopup from './subscribe-popup.js';
import SignPopup from './sign-popup.js';
import FormSubmission from './form-submission.js';
import SearchToggle from './search-toggle.js';
import ProgressBar from './progress-bar.js';

const trackEvent = TrackingCore.trackEvent.bind(TrackingCore);

window.trackEvent = trackEvent;
window.Tracking = Tracking;
window.TRACKING_EVENTS = TRACKING_EVENTS;
window.PopupManager = PopupManager;
window.FormSubmission = FormSubmission;

const SWIPER_SELECTOR = '.gallery-swiper, .gallery-thumbs, .tests-swiper, .providers-mosaic-swiper, .provider-conditions-swiper, .hero-conditions__carousel-wrapper';

/**
 * Community LP — wire every CTA on the page to the magic-link popup.
 *
 * Stamps `data-popup="join-community"` on every `<button>` and `<a class="btn">`
 * inside `<main class="community-lp">` (except those nested in the hidden
 * popup body itself). PopupManager.init() runs right after this and picks
 * the triggers up declaratively, so click handlers and `preventDefault()`
 * come for free — no separate listener needed, and any future button added
 * to a community section is covered automatically.
 */
function prepareCommunityLpJoinTriggers() {
    const root = document.querySelector('main.community-lp, .community-lp');
    if (!root) return;

    const popupHost = document.getElementById('join-community-popup-content');
    const candidates = root.querySelectorAll('button, a.btn');

    candidates.forEach((el) => {
        if (popupHost && popupHost.contains(el)) return;
        if (el.hasAttribute('data-popup')) return;
        const href = el.getAttribute('href');
        if (href && href.startsWith('#')) return;
        el.setAttribute('data-popup', 'join-community');
    });
}

/**
 * If the user lands on /community/ via the global header "Join Community"
 * CTA (which links to /community/#join), open the magic-link popup right
 * away. The hash is then cleaned up so it doesn't survive bookmarks or
 * back/forward navigation.
 *
 * Runs only on pages that actually expose the popup body (community LP),
 * after PopupManager.init() has wired up declarative [data-popup] triggers.
 */
function maybeAutoOpenJoinCommunityPopup() {
    const popupHost = document.getElementById('join-community-popup-content');
    if (!popupHost) return;
    if (window.location.hash !== '#join') return;
    if (!window.PopupManager || typeof window.PopupManager.openPopupFromHTML !== 'function') return;

    try {
        history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch (_) { /* history API unavailable — ignore */ }

    const config = typeof window.PopupManager.getPopupConfig === 'function'
        ? window.PopupManager.getPopupConfig('join-community')
        : {};

    window.PopupManager.openPopupFromHTML('#join-community-popup-content', {
        ...config,
        popupType: 'join-community',
    });
}

/**
 * Community LP eyebrow labels: type the text character-by-character when the
 * label's parent section scrolls into view. One-shot per label, respects
 * `prefers-reduced-motion`, and locks the label's width before clearing so
 * surrounding layout doesn't reflow as letters arrive.
 */
function initCommunityLpTypewriter() {
    const root = document.querySelector('.community-lp');
    if (!root) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const labels = root.querySelectorAll('.label');
    if (!labels.length) return;

    const PER_CHAR_MS = 38;
    const POST_TYPE_CARET_MS = 1200;

    labels.forEach((label) => {
        const original = (label.textContent || '').replace(/\s+/g, ' ').trim();
        if (!original || label.dataset.typewriterReady === '1') return;

        // Lock current width so the section doesn't jiggle while letters arrive.
        const rect = label.getBoundingClientRect();
        if (rect.width > 0) {
            label.style.minWidth = Math.ceil(rect.width) + 'px';
        }

        label.dataset.typewriterReady = '1';
        label.classList.add('is-typewriter');
        label.setAttribute('aria-label', original);

        const typed = document.createElement('span');
        typed.className = 'label__typed';
        typed.setAttribute('aria-hidden', 'true');

        const caret = document.createElement('span');
        caret.className = 'label__caret';
        caret.setAttribute('aria-hidden', 'true');

        label.textContent = '';
        label.appendChild(typed);
        label.appendChild(caret);

        let started = false;
        const typeOut = () => {
            if (started) return;
            started = true;
            let i = 0;
            const tick = () => {
                if (i >= original.length) {
                    label.classList.add('is-typewriter-done');
                    setTimeout(() => {
                        if (caret.parentNode) caret.parentNode.removeChild(caret);
                        label.classList.add('is-typewriter-finished');
                    }, POST_TYPE_CARET_MS);
                    return;
                }
                i += 1;
                typed.textContent = original.slice(0, i);
                setTimeout(tick, PER_CHAR_MS);
            };
            tick();
        };

        const target = label.closest('section') || label.parentElement || label;

        const inView = () => {
            const r = target.getBoundingClientRect();
            const vh = window.innerHeight || document.documentElement.clientHeight;
            return r.top < vh * 0.92 && r.bottom > 0;
        };

        if (inView()) {
            requestAnimationFrame(typeOut);
            return;
        }

        const io = new IntersectionObserver(
            (entries, obs) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    typeOut();
                    obs.disconnect();
                });
            },
            { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.15 }
        );

        io.observe(target);
    });
}

function loadSwiperIfNeeded() {
    if (!document.querySelector(SWIPER_SELECTOR)) return;
    import(/* webpackChunkName: "swiper" */ './swiper.js').then((m) => {
        window.SwiperManager = m.default;
        setTimeout(() => m.default.initAllSwipers(), 300);
    });
}

function loadOptionalModules() {
    const jobs = [];

    if (document.querySelector('.faq-v2')) {
        jobs.push(import(/* webpackChunkName: "faq-v2" */ './faq-v2.js').then((m) => {
            window.FAQv2 = m.default;
            m.default.init();
        }));
    }

    if (document.querySelector('[data-promo-banner]')) {
        jobs.push(import(/* webpackChunkName: "promo" */ './promo-banner.js').then((m) => {
            m.default.init();
        }));
    }

    if (document.querySelector('.badge--verified, .badge--unclaimed, .badge--claimed, [data-verified-tooltip], [data-claimed-tooltip], [data-badge-tooltip-type="verified"], [data-badge-tooltip-type="claimed"], [data-badge-tooltip-type="unclaimed"]')) {
        jobs.push(import(/* webpackChunkName: "badge-tooltip" */ './verified-badge-tooltip.js').then((m) => {
            m.default.init();
        }));
    }

    if (document.querySelector('[data-search-results]')) {
        jobs.push(import(/* webpackChunkName: "search-more" */ './search-load-more.js').then((m) => {
            m.default.init();
        }));
    }

    if (document.querySelector('[data-tools-hub]')) {
        jobs.push(import(/* webpackChunkName: "tools-hub" */ './tools/tools-hub.js').then((m) => {
            m.initToolsHub();
        }));
    } else if (document.querySelector('[data-tools-substance-root]')) {
        // Toolkit page: same substance pills as /tools Preparation tab.
        jobs.push(import(/* webpackChunkName: "tools-hub" */ './tools/tools-hub.js').then((m) => {
            m.initSubstancePills();
        }));
    }

    if (document.querySelector('[data-news-page]')) {
        jobs.push(import(/* webpackChunkName: "news-page" */ './news-page.js').then((m) => {
            m.initNewsPage();
        }));
    }

    if (document.querySelector('[data-stories-page]')) {
        jobs.push(import(/* webpackChunkName: "stories-page" */ './stories-page.js').then((m) => {
            m.initStoriesPage();
        }));
    }

    if (document.querySelector('[data-hero-slider]')) {
        jobs.push(import(/* webpackChunkName: "home-hero-slider" */ './home-hero-slider.js').then((m) => {
            m.default.init();
        }));
    }

    return Promise.all(jobs);
}

function loadAuthIfNeeded() {
    if (!document.querySelector('[data-signin-form]')) return;
    import(/* webpackChunkName: "auth" */ './auth.js').then((m) => {
        if (!window.SignIn) window.SignIn = m.default;
        m.default.init();
    });
}

const Menu = {
    initOffCanvasMenu() {
        const offCanvasToggleButtons = document.querySelectorAll('.toggle--off-canvas--menu');
        const offCanvas = document.querySelector('.off-canvas--menu');
        if (offCanvasToggleButtons.length === 0 || !offCanvas) return;

        const nav = offCanvas.querySelector('.off-canvas__navigation');
        const actions = offCanvas.querySelector('.off-canvas__actions');
        const navItems = [...offCanvas.querySelectorAll('.off-canvas__nav-item')];

        const updateActionsShadow = () => { if (actions && nav) actions.classList.toggle('off-canvas__actions--scrolled', nav.scrollTop > 0); };

        const switchToMenuMode = () => {
            document.body.classList.remove('off-canvas-opened-by-search');
            const firstWithSublist = navItems.find(item => item.querySelector('.off-canvas__nav-sublist'));
            if (firstWithSublist) firstWithSublist.classList.remove('is-collapsed');
        };

        const toggle = () => {
            const currentlyOpen = offCanvas.classList.contains('off-canvas--visible');
            if (currentlyOpen && document.body.classList.contains('off-canvas-opened-by-search')) { switchToMenuMode(); return; }
            if (!currentlyOpen) {
                if (nav) nav.scrollTop = 0;
                updateActionsShadow();
                const currentSection = nav?.getAttribute('data-current-section') ?? '';
                navItems.forEach(item => {
                    if (item.querySelector('.off-canvas__nav-sublist')) {
                        item.classList.add('is-collapsed');
                        if (currentSection && item.dataset.section === currentSection) item.classList.remove('is-collapsed');
                    }
                });
            }
            const isOpen = offCanvas.classList.toggle('off-canvas--visible');
            document.body.classList.toggle('off-canvas-menu-open', isOpen);
            if (!isOpen) {
                document.body.classList.remove('off-canvas-opened-by-search');
                offCanvas.querySelectorAll('.off-canvas__nav-other-toggle').forEach(btn => {
                    const targetId = btn.getAttribute('data-nav-other');
                    offCanvas.querySelectorAll(`[data-nav-other-target="${targetId}"]`).forEach(el => { el.hidden = true; });
                    btn.setAttribute('aria-expanded', 'false');
                    btn.classList.remove('is-expanded');
                });
                navItems.forEach(item => item.classList.remove('is-collapsed'));
            }
        };

        const runSectionToggle = (item, index, e, isTouch) => {
            const link = item.querySelector('.off-canvas__nav-link');
            if (link && (e.target === link || link.contains(e.target))) return;
            if (isTouch) e.preventDefault();
            const wasCollapsed = item.classList.contains('is-collapsed');
            if (wasCollapsed) { navItems.forEach(other => other.classList.add('is-collapsed')); item.classList.remove('is-collapsed'); }
            else item.classList.add('is-collapsed');
            if (nav && !item.classList.contains('is-collapsed')) {
                const stickyOffset = index * 48;
                if (nav.scrollTop > item.offsetTop - stickyOffset) nav.scrollTo({ top: Math.max(0, item.offsetTop - stickyOffset), behavior: 'smooth' });
            }
        };

        navItems.forEach((item, index) => {
            const row = item.querySelector('.off-canvas__nav-row');
            if (!row || !item.querySelector('.off-canvas__nav-sublist')) return;
            row.addEventListener('touchend', e => runSectionToggle(item, index, e, true), { passive: false });
            row.addEventListener('click', e => runSectionToggle(item, index, e, false));
        });

        if (nav) nav.addEventListener('scroll', updateActionsShadow);
        offCanvasToggleButtons.forEach(btn => btn.addEventListener('click', toggle));

        const expandMenuBtn = offCanvas.querySelector('[data-off-canvas-expand-menu]');
        if (expandMenuBtn) expandMenuBtn.addEventListener('click', switchToMenuMode);

        const closeMenu = () => {
            offCanvas.classList.remove('off-canvas--visible');
            document.body.classList.remove('off-canvas-menu-open', 'off-canvas-opened-by-search');
            offCanvas.querySelectorAll('.off-canvas__nav-other-toggle').forEach(btn => {
                const targetId = btn.getAttribute('data-nav-other');
                offCanvas.querySelectorAll(`[data-nav-other-target="${targetId}"]`).forEach(el => { el.hidden = true; });
                btn.setAttribute('aria-expanded', 'false');
                btn.classList.remove('is-expanded');
            });
            navItems.forEach(item => item.classList.remove('is-collapsed'));
        };

        const mobileSearchFocusBtn = offCanvas.querySelector('[data-mobile-search-focus]');
        const mobileSearchInput = offCanvas.querySelector('[data-mobile-search-input]');
        if (mobileSearchFocusBtn && mobileSearchInput) {
            mobileSearchFocusBtn.addEventListener('click', () => {
                document.body.classList.add('off-canvas-opened-by-search');
                navItems.forEach(item => item.classList.add('is-collapsed'));
                mobileSearchInput.focus();
            });
        }

        const closeBtn = offCanvas.querySelector('[data-off-canvas-close]');
        if (closeBtn) closeBtn.addEventListener('click', closeMenu);
        offCanvas.querySelectorAll('.off-canvas__nav-other-toggle').forEach(btn => {
            btn.addEventListener('click', () => {
                const targetId = btn.getAttribute('data-nav-other');
                const items = offCanvas.querySelectorAll(`[data-nav-other-target="${targetId}"]`);
                const expanded = items[0]?.hidden !== false;
                items.forEach(el => { el.hidden = !expanded; });
                btn.setAttribute('aria-expanded', expanded);
                btn.classList.toggle('is-expanded', expanded);
            });
        });
    }
};

const Accordion = {
    init() {
        document.querySelectorAll('.accordion__header').forEach(header => {
            header.addEventListener('click', (e) => { e.preventDefault(); this.toggleAccordion(header); });
            header.style.cursor = 'pointer';
        });
    },
    toggleAccordion(header) {
        const body = header.closest('.accordion')?.querySelector('.accordion__body');
        const arrow = header.querySelector('img');
        if (!body) return;
        const isOpen = body.classList.contains('accordion__body--open');
        body.classList.toggle('accordion__body--open', !isOpen);
        if (arrow) arrow.style.transform = isOpen ? 'rotate(0deg)' : 'rotate(180deg)';
    }
};

const ArticleFaqAccordion = {
    init() {
        document.body.addEventListener('click', (e) => {
            const question = e.target.closest('.faq-item__question');
            if (!question) return;
            const item = question.closest('.faq-item');
            const list = question.closest('.faq-list');
            if (!item || !list) return;
            const isActive = item.classList.contains('--active');
            list.querySelectorAll('.faq-item').forEach((i) => i.classList.remove('--active'));
            if (!isActive) item.classList.add('--active');
        });
    }
};

const TagChipsToggle = {
    init() {
        document.querySelectorAll('.tag-chips__toggle, .provider-single__tags-toggle').forEach(button => {
            button.addEventListener('click', (e) => { e.preventDefault(); this.toggle(button); });
        });
    },
    toggle(button) {
        const group = button.closest('.tag-chips__group, .provider-single__tags-group');
        if (!group) return;
        const isExpanded = group.getAttribute('data-expanded') === 'true';
        if (isExpanded) { group.removeAttribute('data-expanded'); button.setAttribute('aria-expanded', 'false'); }
        else { group.setAttribute('data-expanded', 'true'); button.setAttribute('aria-expanded', 'true'); }
    }
};

const Tabs = {
    init() {
        document.querySelectorAll('.tabs').forEach(container => {
            const tabButtons = container.querySelectorAll('.tabs__button');
            const tabContents = container.querySelectorAll('.tabs__content[data-tab]');
            tabButtons.forEach(button => {
                button.addEventListener('click', () => {
                    const targetTab = button.getAttribute('data-tab');
                    tabButtons.forEach(btn => btn.classList.remove('tabs__button--active'));
                    tabContents.forEach(content => content.classList.remove('tabs__content--active'));
                    button.classList.add('tabs__button--active');
                    container.querySelector(`.tabs__content[data-tab="${targetTab}"]`)?.classList.add('tabs__content--active');
                });
            });
            if (tabButtons.length > 0) tabButtons[0].click();
        });
    }
};

const HeaderScrollToggle = {
    init() {
        const header = document.querySelector('.header');
        if (!header) return;
        const mobileQuery = window.matchMedia('(max-width: 767px)');
        const tocMobile = document.querySelector('[data-toc-mobile]');
        const headerTocToggle = document.querySelector('[data-header-toc-toggle]');
        let lastY = window.scrollY, ticking = false, scrollListener = null;

        const isTocOpen = () => tocMobile && tocMobile.classList.contains('is-open');
        const closeTocDropdown = () => { if (tocMobile) tocMobile.classList.remove('is-open'); if (headerTocToggle) headerTocToggle.setAttribute('aria-expanded', 'false'); };

        const update = () => {
            ticking = false;
            const currentY = window.scrollY;
            if (mobileQuery.matches && headerTocToggle) {
                const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                headerTocToggle.style.setProperty('--toc-scroll-fill', String(maxScroll > 0 ? Math.min(100, (currentY / maxScroll) * 100) : 0));
            }
            if (currentY <= 10) { header.classList.remove('header--hidden'); lastY = currentY; return; }
            if (isTocOpen()) { lastY = currentY; return; }
            if (currentY > lastY + 4 && currentY > 50) { header.classList.add('header--hidden'); closeTocDropdown(); }
            else if (currentY < lastY - 4) header.classList.remove('header--hidden');
            lastY = currentY;
        };

        const onScroll = () => { if (!ticking) { window.requestAnimationFrame(update); ticking = true; } };
        const setupScrollListener = () => {
            if (mobileQuery.matches) { if (!scrollListener) { scrollListener = onScroll; window.addEventListener('scroll', scrollListener, { passive: true }); } update(); }
            else { header.classList.remove('header--hidden'); if (headerTocToggle) headerTocToggle.style.removeProperty('--toc-scroll-fill'); if (scrollListener) { window.removeEventListener('scroll', scrollListener); scrollListener = null; } }
        };
        setupScrollListener();
        mobileQuery.addEventListener('change', setupScrollListener);
    }
};

const InputLabels = {
    init() {
        document.querySelectorAll('.input-v2').forEach(input => {
            const placeholder = input.getAttribute('placeholder');
            if (placeholder && !input.parentNode.querySelector('.input-v2__label')) {
                const label = document.createElement('span');
                label.className = 'input-v2__label';
                label.textContent = placeholder;
                input.parentNode.insertBefore(label, input.nextSibling);
            }
        });
    }
};

window.InputLabels = InputLabels;
window.SignPopup = SignPopup;

function initCoreUi() {
    SignPopup.init();
    prepareCommunityLpJoinTriggers();
    PopupManager.init();
    maybeAutoOpenJoinCommunityPopup();
    initCommunityLpTypewriter();
    loadSwiperIfNeeded();
    loadOptionalModules();
    loadAuthIfNeeded();

    InputLabels.init();
    Menu.initOffCanvasMenu();
    SearchToggle.init();
    Accordion.init();
    Tabs.init();
    TagChipsToggle.init();
    ProgressBar.init();
    FormSubmission.init();
    SubscribePopup.init();
    ArticleFaqAccordion.init();
    HeaderScrollToggle.init();
    Tracking.init();

    if (document.querySelector('[data-account-form], #delete-account-btn, #delete-test-results-btn')) {
        import(/* webpackChunkName: "account" */ './account.js').then(m => m.initAccount());
    }

    if (document.querySelector('.article-v2, .single-post, [data-toc]')) {
        import(/* webpackChunkName: "article" */ './references.js').then(m => m.initReferences());
        import(/* webpackChunkName: "article" */ './toc.js').then(m => m.default.init());
        import(/* webpackChunkName: "article" */ './reference-popup.js').then(m => m.default.init());
        import(/* webpackChunkName: "article" */ './reactions.js').then(m => m.default.init());
        import(/* webpackChunkName: "article" */ './expert-reviewed-link.js').then(m => m.default.init());
        import(/* webpackChunkName: "article" */ './sticky-cta.js').then(m => m.default.init());
    } else if (document.querySelector('.research-overview .reference-anchor-wrap')) {
        import(/* webpackChunkName: "article" */ './reference-popup.js').then(m => m.default.init());
    }

    // Share modal: needed anywhere a `.share-trigger` button is rendered
    // (single articles, the /library tools hub, etc.). Loaded outside the
    // article-only block so non-article pages (like the tools hub) get the
    // share popup too.
    if (document.querySelector('.share-trigger')) {
        import(/* webpackChunkName: "article" */ './share.js').then(m => m.default.init());
    }

    if (document.querySelector('[data-research-app]')) {
        import(/* webpackChunkName: "research" */ './research-pagination.js').then(m => m.default.init());
        import(/* webpackChunkName: "research" */ './research-filters.js').then(m => m.default.init());
    }

    if (document.querySelector('#test-options-form, .test-landing__link')) {
        import(/* webpackChunkName: "test" */ './test-page.js').then(m => m.initTestPage());
    }

    if (document.querySelector('[data-podcast-player], [data-podcast-tabs], [data-youtube-id], [data-podcast-show-switch]')) {
        import(/* webpackChunkName: "podcast" */ './podcast.js').then(m => m.initPodcast());
    }

    if (document.querySelector('.home-v4-cta__lottie')) {
        import(/* webpackChunkName: "home-v4-cta" */ './home-v4-cta.js').then((m) => m.initHomeCtaMotion());
    }

    initHomeHeroGifs();
    initHomeHeroVideos();
}

/**
 * Homepage hero: play episode GIF on hover (desktop) / tap-hold (mobile).
 */
function initHomeHeroGifs() {
    const hosts = document.querySelectorAll('[data-gif-src]');
    if (!hosts.length) {
        return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    const fineHover = window.matchMedia('(hover: hover) and (pointer: fine)');

    hosts.forEach((host) => {
        const gifUrl = host.getAttribute('data-gif-src');
        const gifImg = host.querySelector('.home-v4-gif');
        if (!gifUrl || !gifImg) {
            return;
        }

        const play = () => {
            if (gifImg.getAttribute('src') !== gifUrl) {
                gifImg.src = gifUrl;
            } else {
                gifImg.src = '';
                gifImg.src = gifUrl;
            }
            host.classList.add('is-gif-playing');
        };

        const stop = () => {
            host.classList.remove('is-gif-playing');
            gifImg.removeAttribute('src');
        };

        if (fineHover.matches) {
            host.addEventListener('mouseenter', play);
            host.addEventListener('mouseleave', stop);
            host.addEventListener('focus', play);
            host.addEventListener('blur', stop);
            return;
        }

        host.addEventListener('pointerdown', play);
        host.addEventListener('pointerup', stop);
        host.addEventListener('pointercancel', stop);
        host.addEventListener('pointerleave', stop);
    });
}

/**
 * Homepage featured hero: play hover video on hover (desktop) / tap-hold (mobile).
 *
 * Heavy files (20–50 MB) cannot start on first hover if src is attached then
 * discarded on mouseleave. Warm the buffer while the card is on screen and
 * keep it between hovers.
 */
function initHomeHeroVideos() {
    const hosts = document.querySelectorAll('[data-video-src]');
    if (!hosts.length) {
        return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    const fineHover = window.matchMedia('(hover: hover) and (pointer: fine)');

    hosts.forEach((host) => {
        const videoUrl = host.getAttribute('data-video-src');
        const video = host.querySelector('.home-v4-hover-video');
        if (!videoUrl || !video) {
            return;
        }

        let wantsPlay = false;

        const attachSrc = () => {
            if (video.getAttribute('src') === videoUrl) {
                return;
            }
            video.preload = 'auto';
            video.src = videoUrl;
            video.load();
        };

        const tryPlay = () => {
            if (!wantsPlay) {
                return;
            }

            attachSrc();

            const playPromise = video.play();
            if (playPromise && typeof playPromise.then === 'function') {
                playPromise.then(() => {
                    if (wantsPlay) {
                        host.classList.add('is-video-playing');
                    }
                }).catch(() => {});
            }
        };

        const play = () => {
            wantsPlay = true;
            tryPlay();
        };

        const stop = () => {
            wantsPlay = false;
            host.classList.remove('is-video-playing');
            video.pause();
            if (video.readyState >= 1) {
                try {
                    video.currentTime = 0;
                } catch (e) {
                    // Ignore seek errors while the first bytes are still loading.
                }
            }
        };

        video.addEventListener('canplay', tryPlay);
        video.addEventListener('playing', () => {
            if (wantsPlay) {
                host.classList.add('is-video-playing');
            }
        });

        const warm = () => attachSrc();
        const startWarm = () => {
            if ('requestIdleCallback' in window) {
                window.requestIdleCallback(warm, { timeout: 1200 });
            } else {
                window.setTimeout(warm, 400);
            }
        };

        if ('IntersectionObserver' in window) {
            const io = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        startWarm();
                        io.disconnect();
                    }
                });
            }, { rootMargin: '200px' });
            io.observe(host);
        } else {
            startWarm();
        }

        const warmZone = host.closest('.home-v4-hero__featured') || host;
        warmZone.addEventListener('pointerenter', warm, { passive: true });

        if (fineHover.matches) {
            host.addEventListener('mouseenter', play);
            host.addEventListener('mouseleave', stop);
            host.addEventListener('focus', play);
            host.addEventListener('blur', stop);
            return;
        }

        host.addEventListener('pointerdown', play);
        host.addEventListener('pointerup', stop);
        host.addEventListener('pointercancel', stop);
        host.addEventListener('pointerleave', stop);
    });
}

document.addEventListener('DOMContentLoaded', () => {
    const gatedReady = document.querySelector('.gated-file-block')
        ? import(/* webpackChunkName: "gated-file" */ './gated-file-popup.js').then((m) => {
            window.GatedFilePopup = m.default;
            m.default.init();
        })
        : Promise.resolve();

    gatedReady.then(() => initCoreUi());
});
